import { Controller } from '@hotwired/stimulus'
import { useRecaptcha } from '../mixins/useRecaptcha'

export default class extends Controller {
  static targets = ['input']
  static values = {
    action: String,
    // What the visitor is told when no token can be obtained. Passed in from the template
    // rather than written here, because the text is translated and this app is multi-tenant;
    // every form carrying this controller supplies it, and a guard test says so.
    errorMessage: String
  }

  // Fix infinite submitting via requestSubmit
  isSubmitting = false

  connect () {
    useRecaptcha(this)

    // Warm Google's script up on the first touch of the form, so the submit pays for a
    // token and nothing else. Not on `connect`: the add-company modal is rendered by the
    // LAYOUT, so this controller is on every page of the site, and warming up there would
    // put a third-party script and its badge in front of everyone who is only reading
    // vacancies. Filling a field in is the point where a submit becomes likely and there
    // are still seconds of typing left to spend on the download.
    this.boundPreload = this.preload.bind(this)
    this.element.addEventListener('focusin', this.boundPreload, { once: true })
  }

  disconnect () {
    this.element.removeEventListener('focusin', this.boundPreload)
  }

  preload () {
    // A rejection is not an error yet — `handleSubmit` asks again and reports it there.
    this.recaptcha.preload()?.catch(() => {})
  }

  async handleSubmit (event) {
    if (this.isSubmitting) return

    // reCAPTCHA not configured (no site key in this environment): stay out of the
    // way entirely and let the form submit natively. Wrapping the submit when there
    // is no token to fetch only risks swallowing it.
    if (!document.querySelector('meta[name=recaptcha-v3-site-key]')?.content) return

    event.preventDefault()

    try {
      const token = await this.recaptcha.getToken(this.actionValue)
      this.recaptcha.setToken(this.inputTarget, token)

      this.isSubmitting = true
      this.element.requestSubmit()
    } catch (e) {
      console.error(e)
      this.#reportFailure()
    }

    this.isSubmitting = false
  }

  // Say it on screen, not only in the console. `preventDefault` above has already cancelled
  // the submit, so when this is silent the press is simply lost: the visitor is left looking
  // at an ordinary, still-enabled button that does nothing, and nothing but a page reload
  // changes that. Reported by the owner 2026-08-27 on /jobs/new/careers, and reproduced on
  // the stand — two presses, two console errors, no request, no pixel moved.
  //
  // Deliberately the same toast the SERVER answers a rejected verification with
  // (`application/recaptcha_failed.turbo_stream.erb` → `FlashMessage::Component`), so the
  // two ways this can fail look identical to the person in front of them. Keep the markup
  // in step with that component; `data-controller="flash"` is what shows it.
  #reportFailure () {
    const container = document.getElementById('flashMessages')

    if (!container || !this.errorMessageValue) return

    const wrapper = document.createElement('div')
    wrapper.className = 'toast-container'
    wrapper.innerHTML =
      '<div class="toast toast-danger" role="alert" aria-live="assertive" aria-atomic="true" ' +
      'data-controller="flash" data-bs-delay="5000"><div class="toast-body"></div></div>'
    // textContent, not interpolation: the message is configuration, but it is still text
    // going into the page and there is no reason for it to be able to carry markup.
    wrapper.querySelector('.toast-body').textContent = this.errorMessageValue

    container.append(wrapper)
  }
}
